// ============================================================================
// HAI Reporting Tool - IndexedDB Database (Dexie)
// Offline-first local storage for HAI surveillance data
// ============================================================================

import Dexie, { Table } from 'dexie';
import type {
  User,
  Ward,
  DeviceSnapshot,
  HAIRecord,
  SSISurveillance,
  PatientRecord,
  DeviceDailyCount,
  SyncQueueItem,
  AuditLog,
  AlertEvent,
  AppMetadata,
  Facility,
  AlertRule,
  HCAISurveillanceCase
} from '@/types';

export class HAIDatabase extends Dexie {
  // Tables
  users!: Table<User, string>;
  facilities!: Table<Facility, string>;
  wards!: Table<Ward, string>;
  deviceSnapshots!: Table<DeviceSnapshot, string>;
  haiRecords!: Table<HAIRecord, string>;
  ssiSurveillance!: Table<SSISurveillance, string>;
  patientRecords!: Table<PatientRecord, string>;
  deviceDailyCounts!: Table<DeviceDailyCount, string>;
  hcaiCases!: Table<HCAISurveillanceCase, string>;
  syncQueue!: Table<SyncQueueItem, string>;
  auditLogs!: Table<AuditLog, string>;
  alertEvents!: Table<AlertEvent, string>;
  alertRules!: Table<AlertRule, string>;
  appMetadata!: Table<AppMetadata, string>;

  constructor() {
    super('HAIReportingDB');

    // Version 1: Original schema
    this.version(1).stores({
      users: 'id, email, role, isActive',
      facilities: 'id, name, isActive',
      wards: 'id, facilityId, name, isActive',
      deviceSnapshots: 'id, wardId, date, [wardId+date]',
      haiRecords: 'id, wardId, periodStart, periodEnd, [wardId+periodStart]',
      ssiSurveillance: 'id, wardId, periodStart, periodEnd, [wardId+periodStart]',
      patientRecords: 'id, wardId, admissionDate, [wardId+admissionDate]',
      deviceDailyCounts: 'id, wardId, date, [wardId+date]',
      syncQueue: 'id, status, entityType, createdAt',
      auditLogs: 'id, userId, entityType, timestamp',
      alertEvents: 'id, wardId, metricType, isAcknowledged, createdAt',
      alertRules: 'id, metricType, wardId, isActive',
      appMetadata: 'id'
    });

    // Version 2: Add hcaiCases table for comprehensive HCAI surveillance cases
    this.version(2).stores({
      users: 'id, email, role, isActive',
      facilities: 'id, name, isActive',
      wards: 'id, facilityId, name, isActive',
      deviceSnapshots: 'id, wardId, date, [wardId+date]',
      haiRecords: 'id, wardId, periodStart, periodEnd, [wardId+periodStart]',
      ssiSurveillance: 'id, wardId, periodStart, periodEnd, [wardId+periodStart]',
      patientRecords: 'id, wardId, admissionDate, [wardId+admissionDate]',
      deviceDailyCounts: 'id, wardId, date, [wardId+date]',
      hcaiCases: 'id, hospital, ward, admissionDate, createdAt',
      syncQueue: 'id, status, entityType, createdAt',
      auditLogs: 'id, userId, entityType, timestamp',
      alertEvents: 'id, wardId, metricType, isAcknowledged, createdAt',
      alertRules: 'id, metricType, wardId, isActive',
      appMetadata: 'id'
    });
  }

  /**
   * Clear all data (for testing or logout)
   */
  async clearAllData(): Promise<void> {
    await this.transaction('rw', this.tables, async () => {
      await Promise.all(this.tables.map(table => table.clear()));
    });
  }

  /**
   * Get app metadata
   */
  async getAppMetadata(): Promise<AppMetadata> {
    const metadata = await this.appMetadata.get('app');
    if (!metadata) {
      const defaultMetadata: AppMetadata = {
        syncStatus: 'idle',
        pendingSyncCount: 0,
        isOnline: navigator.onLine
      };
      await this.appMetadata.put({ ...defaultMetadata, id: 'app' } as any);
      return defaultMetadata;
    }
    return metadata;
  }

  /**
   * Update app metadata
   */
  async updateAppMetadata(updates: Partial<AppMetadata>): Promise<void> {
    const current = await this.getAppMetadata();
    await this.appMetadata.put({ ...current, ...updates, id: 'app' } as any);
  }

  /**
   * Get device snapshots for a ward in a date range
   */
  async getDeviceSnapshotsByWardAndDateRange(
    wardId: string,
    startDate: string,
    endDate: string
  ): Promise<DeviceSnapshot[]> {
    return await this.deviceSnapshots
      .where('[wardId+date]')
      .between([wardId, startDate], [wardId, endDate], true, true)
      .toArray();
  }

  /**
   * Get HAI records for a ward in a date range
   */
  async getHAIRecordsByWardAndDateRange(
    wardId: string,
    startDate: string,
    endDate: string
  ): Promise<HAIRecord[]> {
    return await this.haiRecords
      .where('wardId')
      .equals(wardId)
      .filter(record => {
        return record.periodStart >= startDate && record.periodEnd <= endDate;
      })
      .toArray();
  }

  /**
   * Get SSI surveillance for a ward in a date range
   */
  async getSSISurveillanceByWardAndDateRange(
    wardId: string,
    startDate: string,
    endDate: string
  ): Promise<SSISurveillance[]> {
    return await this.ssiSurveillance
      .where('wardId')
      .equals(wardId)
      .filter(record => {
        return record.periodStart >= startDate && record.periodEnd <= endDate;
      })
      .toArray();
  }

  /**
   * Get pending sync items
   */
  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    return await this.syncQueue
      .where('status')
      .equals('PENDING')
      .sortBy('createdAt');
  }

  /**
   * Get active (unacknowledged) alerts
   */
  async getActiveAlerts(): Promise<AlertEvent[]> {
    return await this.alertEvents
      .where('isAcknowledged')
      .equals(0) // Dexie uses 0 for false
      .reverse()
      .sortBy('createdAt');
  }

  /**
   * Get wards assigned to a user
   */
  async getWardsByIds(wardIds: string[]): Promise<Ward[]> {
    return await this.wards
      .where('id')
      .anyOf(wardIds)
      .and(ward => ward.isActive)
      .toArray();
  }

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.syncQueue.add({ ...item, id });

    // Update metadata
    const pendingCount = await this.syncQueue.where('status').equals('PENDING').count();
    await this.updateAppMetadata({ pendingSyncCount: pendingCount });

    return id;
  }

  /**
   * Mark sync item as complete
   */
  async markSyncItemComplete(itemId: string): Promise<void> {
    await this.syncQueue.update(itemId, {
      status: 'SUCCESS',
      updatedAt: new Date()
    });

    const pendingCount = await this.syncQueue.where('status').equals('PENDING').count();
    await this.updateAppMetadata({ pendingSyncCount: pendingCount });
  }

  /**
   * Mark sync item as failed
   */
  async markSyncItemFailed(itemId: string, error: string): Promise<void> {
    const item = await this.syncQueue.get(itemId);
    if (!item) return;

    await this.syncQueue.update(itemId, {
      status: 'ERROR',
      retries: item.retries + 1,
      lastError: error,
      updatedAt: new Date()
    });
  }

  /**
   * Add audit log entry
   */
  async addAuditLog(log: Omit<AuditLog, 'id'>): Promise<string> {
    const id = crypto.randomUUID();
    await this.auditLogs.add({ ...log, id });
    return id;
  }

  /**
   * Get device daily counts for a specific date
   */
  async getDeviceDailyCountByDate(wardName: string, date: string): Promise<DeviceDailyCount | undefined> {
    return await this.deviceDailyCounts.get(`${wardName}_${date}`);
  }

  /**
   * Save or update device daily counts for a date
   */
  async saveDeviceDailyCount(data: Omit<DeviceDailyCount, 'createdAt' | 'updatedAt'>): Promise<void> {
    const id = `${data.wardName}_${data.date}`;
    const existing = await this.deviceDailyCounts.get(id);

    const record: DeviceDailyCount = {
      ...data,
      id,
      createdAt: existing?.createdAt || new Date(),
      updatedAt: new Date()
    };

    await this.deviceDailyCounts.put(record);
  }
}

// Create and export singleton instance
export const db = new HAIDatabase();
